import {
	ChangeEvent, FC, KeyboardEvent, useMemo, useState,
} from 'react';
import { debounce } from 'lodash';
import {
	Button, Input, InputSize, SearchMediumIcon, useOpen, Zoom,
} from '@aleph/shared-components';
import { AnimatePresence, motion } from 'framer-motion';

type SpotifyHeaderSearchParams = {
	search: string
	setSearch: (search: string) => void
	placeholder?: string
};

export const SpotifyHeaderSearch: FC<SpotifyHeaderSearchParams> = ({ search, setSearch, placeholder }) => {
	const [searchOpen, { onOpen, onClose }] = useOpen(false);
	const [searchValue, setSearchValue] = useState(search);

	const debouncedSetSearch = useMemo(() => debounce(setSearch, 300), [setSearch]);

	const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
		debouncedSetSearch(e.target.value);
	};

	const onBlur = (e) => {
		if (e.target.value === '') {
			onClose();
		}
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			onClose();
			setSearch('');
		}
	};

	return (
		<AnimatePresence mode="wait">
			{
				searchOpen ? <motion.div
					key="input"
					className="flex items-center justify-end relative py-[4px]"
					initial={{ width: 0, opacity: 0 }}
					animate={{ width: '50%', opacity: 1 }}
					exit={{ width: 0, opacity: 0 }}
					transition={{ type: 'tween', duration: 0.2 }}
				>
					<Input
						data-testid="header-search-input"
						size={InputSize.MEDIUM}
						className="w-full"
						autoFocus
						value={searchValue}
						onChange={onSearchChange}
						onBlur={onBlur}
						onKeyDown={onKeyDown}
						placeholder={placeholder}
						prefix={<SearchMediumIcon />}
						clearable
					/>
				</motion.div> : <div
					key="button"
					className="flex items-center py-[4px]"
				>
					<Button
						data-testid="header-search-button"
						leftNode={<Zoom />}
						onClick={onOpen}
						size="large"
						appearance="ghost"
						color="gray"
					/>
				</div>
			}
		</AnimatePresence>
	);
};
